// =======================================
// DATA TYPES
// =======================================

interface TeamRef {
  id: string;
  first_name: string;
  last_name: string;
}

export type CorBand = 'LOW' | 'MEDIUM' | 'MEDIUM-HIGH' | 'HIGH';

/**
 * COR item as returned by get_filtered_cors (denormalized via cor_list_view).
 */
export interface Cor {
  id: number;
  reference_seq: number;
  reference: string; // "COR-1"
  type: 'Change' | 'Opportunity' | 'Risk';
  title: string;
  raised_by: TeamRef | null;
  owner: TeamRef | null;
  date_raised: string;
  description: string | null;
  opportunity_component: string | null;
  risk_component: string | null;
  likelihood: number | null;
  consequence: number | null;
  initial_rating: number | null;
  initial_band: CorBand | null;
  management_approach: CorApproach | null;
  controls_actions: string | null;
  target_date: string | null;
  residual_likelihood: number | null;
  residual_consequence: number | null;
  residual_rating: number | null;
  residual_band: CorBand | null;
  status: 'Open' | 'In Progress' | 'Closed' | 'Monitoring';
  closure_notes: string | null;
  created_at: string;
  updated_at?: string;
}

// =======================================
// COLUMN CONFIG
// =======================================

export interface CorColumnConfig {
  header: string;
  component?: 'TypeBadge' | 'StatusBadge' | 'RatingBadge' | 'ResidualRatingBadge' | 'ApproachTag' | 'DateColumn' | 'TitleColumn' | 'OwnerColumn';
  sortable?: boolean;
}

/** Table columns for the COR register (in display order). */
export const corColumns: Record<string, CorColumnConfig> = {
  reference: { header: 'COR #', sortable: true },
  type: { header: 'Type', component: 'TypeBadge' },
  date_raised: { header: 'Raised', component: 'DateColumn', sortable: true },
  title: { header: 'Title', component: 'TitleColumn' },
  initial_rating: { header: 'Initial', component: 'RatingBadge', sortable: true },
  management_approach: { header: 'Approach', component: 'ApproachTag' },
  residual_rating: { header: 'Residual', component: 'ResidualRatingBadge' },
  owner: { header: 'Owner', component: 'OwnerColumn' },
  status: { header: 'Status', component: 'StatusBadge', sortable: true },
  target_date: { header: 'Target', component: 'DateColumn', sortable: true },
};

/** Per-status counts returned in the get_filtered_cors `summary`. */
export interface CorSummary {
  open_count: number;
  in_progress_count: number;
  closed_count: number;
  monitoring_count: number;
  total_count: number;
}

// =======================================
// CONSTANTS AND OPTIONS
// =======================================

export const COR_TYPE_OPTIONS = ['Change', 'Opportunity', 'Risk'] as const;
export type CorType = typeof COR_TYPE_OPTIONS[number];

export const COR_STATUS_OPTIONS = ['Open', 'In Progress', 'Closed', 'Monitoring'] as const;
export type CorStatus = typeof COR_STATUS_OPTIONS[number];

/** 5T's management framework (Risk Mgmt doc §5). */
export const COR_APPROACH_OPTIONS = ['Tolerate', 'Treat', 'Track', 'Transfer', 'Terminate'] as const;
export type CorApproach = typeof COR_APPROACH_OPTIONS[number];

export const COR_RATING_BANDS: CorBand[] = ['LOW', 'MEDIUM', 'MEDIUM-HIGH', 'HIGH'];

/** Likelihood scale (doc §4.1) — value 1–5 with descriptor. */
export const LIKELIHOOD_OPTIONS = [
  { value: '1', label: '1 — Rare' },
  { value: '2', label: '2 — Unlikely' },
  { value: '3', label: '3 — Possible' },
  { value: '4', label: '4 — Likely' },
  { value: '5', label: '5 — Almost Certain' },
];

/** Consequence scale (doc §4.2) — value 1–5 with descriptor. */
export const CONSEQUENCE_OPTIONS = [
  { value: '1', label: '1 — Minor' },
  { value: '2', label: '2 — Moderate' },
  { value: '3', label: '3 — Significant' },
  { value: '4', label: '4 — Major' },
  { value: '5', label: '5 — Critical' },
];

export const DEFAULT_PAGE_SIZE = 50;
export const SEARCH_DEBOUNCE_DELAY = 300;

// --- The three dimensions (Change / Opportunity / Risk) ----------------------
// Each item captures all three, but the dimension matching the item's primary
// `type` is shown first and required (the others follow as "… Component").
// Backed by: Change → description, Opportunity → opportunity_component,
// Risk → risk_component.

export type CorDimKey = 'description' | 'opportunity_component' | 'risk_component';

const DIM_META: Record<CorType, { key: CorDimKey; placeholder: string }> = {
  Change: { key: 'description', placeholder: 'Describe the planned change...' },
  Opportunity: { key: 'opportunity_component', placeholder: 'Describe the opportunity — potential benefits or gains...' },
  Risk: { key: 'risk_component', placeholder: 'Describe the risk — potential loss, harm, or adverse outcome...' },
};

export interface CorDimensionField {
  key: CorDimKey;
  label: string;
  primary: boolean;
  placeholder: string;
}

/** The three dimension fields, ordered with the item's primary type first. */
export function corDimensionFields(type: CorType): CorDimensionField[] {
  const ordered: CorType[] = [type, ...COR_TYPE_OPTIONS.filter(t => t !== type)];
  return ordered.map((t, i) => ({
    key: DIM_META[t].key,
    label: i === 0 ? t : `${t} Component`,
    primary: i === 0,
    placeholder: DIM_META[t].placeholder,
  }));
}

/** The DB column for the primary dimension of a given type (the required field). */
export function primaryDimensionKey(type: CorType): CorDimKey {
  return DIM_META[type].key;
}

/** Risk band from a Likelihood×Consequence score (doc §4.4). Mirrors the SQL CASE. */
export function riskBand(score: number | null | undefined): CorBand | null {
  if (score == null) return null;
  if (score <= 3) return 'LOW';
  if (score <= 6) return 'MEDIUM';
  if (score <= 12) return 'MEDIUM-HIGH';
  return 'HIGH';
}

// =======================================
// SHARED MODAL FORM STATE
// =======================================

/** Editable form state shared by AddCorModal and CorDetailModal (via CorFormFields). */
export interface CorFormData {
  type: 'Change' | 'Opportunity' | 'Risk';
  title: string;
  raised_by: string;
  date_raised: string;
  description: string;
  opportunity_component: string;
  risk_component: string;
  likelihood: string;
  consequence: string;
  management_approach: string;
  controls_actions: string;
  owner: string;
  target_date: string;
  residual_likelihood: string;
  residual_consequence: string;
  status: string;
  closure_notes: string;
}

export const emptyCorForm = (): CorFormData => ({
  type: 'Risk',
  title: '',
  raised_by: '',
  date_raised: new Date().toISOString().split('T')[0],
  description: '',
  opportunity_component: '',
  risk_component: '',
  likelihood: '',
  consequence: '',
  management_approach: '',
  controls_actions: '',
  owner: '',
  target_date: '',
  residual_likelihood: '',
  residual_consequence: '',
  status: 'Open',
  closure_notes: '',
});

/** Inline field-level validation errors. */
export interface CorFormErrors {
  type?: string;
  title?: string;
  description?: string;
  opportunity_component?: string;
  risk_component?: string;
}

/**
 * Required-field validation (Minimal): Type, Title, and the PRIMARY dimension
 * for the chosen type (Change → description, Opportunity → opportunity_component,
 * Risk → risk_component). The other fields are filled later during the monthly
 * management review.
 */
export function validateCor(f: CorFormData): CorFormErrors {
  const e: CorFormErrors = {};
  if (!f.type) e.type = 'Type is required';
  if (!f.title.trim()) e.title = 'Title is required';
  const primKey = primaryDimensionKey(f.type);
  if (!f[primKey].trim()) e[primKey] = `${f.type} description is required`;
  return e;
}

// --- Persistence mapping (single source of truth for form → DB columns) ---

const numOrNull = (s: string): number | null => (s ? parseInt(s, 10) : null);
const textOrNull = (s: string): string | null => (s.trim() ? s.trim() : null);

/** Normalised column values — used directly as the edit UPDATE payload. */
export function corColumnValues(f: CorFormData) {
  return {
    type: f.type,
    title: f.title.trim(),
    raised_by: f.raised_by || null,
    date_raised: f.date_raised || null,
    description: textOrNull(f.description),
    opportunity_component: textOrNull(f.opportunity_component),
    risk_component: textOrNull(f.risk_component),
    likelihood: numOrNull(f.likelihood),
    consequence: numOrNull(f.consequence),
    management_approach: f.management_approach || null,
    controls_actions: textOrNull(f.controls_actions),
    owner: f.owner || null,
    target_date: f.target_date || null,
    residual_likelihood: numOrNull(f.residual_likelihood),
    residual_consequence: numOrNull(f.residual_consequence),
    status: f.status,
    closure_notes: textOrNull(f.closure_notes),
  };
}

/** Params for the create_cor_item RPC (create path). */
export function buildCreateParams(f: CorFormData) {
  const v = corColumnValues(f);
  return {
    p_type: v.type,
    p_title: v.title,
    p_raised_by: v.raised_by,
    p_date_raised: f.date_raised,
    p_description: v.description,
    p_opportunity_component: v.opportunity_component,
    p_risk_component: v.risk_component,
    p_likelihood: v.likelihood,
    p_consequence: v.consequence,
    p_management_approach: v.management_approach,
    p_controls_actions: v.controls_actions,
    p_owner: v.owner,
    p_target_date: v.target_date,
    p_residual_likelihood: v.residual_likelihood,
    p_residual_consequence: v.residual_consequence,
    p_status: v.status || 'Open',
    p_closure_notes: v.closure_notes,
  };
}
